function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { blue20, blue30, grey3, spacing4, spacing16, boxShadowRaised, lineHeight } from "@churchofjesuschrist/eden-style-constants";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { RangeInputContext } from "./rangeInputState";
const handleCss = css(["position:relative;appearance:none;border:none;-ms-border:0;background:", ";width:", ";height:", ";border-radius:50%;box-shadow:", ";z-index:3;"], _ref => {
  let {
    inverted
  } = _ref;
  return inverted ? blue20 : blue30;
}, spacing16, spacing16, boxShadowRaised);
export const InternalUseOnlyRangeInput = styled.input.withConfig({
  componentId: "sc-9nm6bc-0"
})(["line-height:", ";appearance:none;width:100%;height:", ";margin:0.375rem 0;cursor:pointer;background:linear-gradient( to right,", " 0%,", " var(--lower-range-percentage),", " var(--lower-range-percentage),", " 100% );&::-moz-focus-outer{border:0;}", " &::-webkit-slider-thumb{", ";}&::-moz-range-thumb{", ";}&::-ms-thumb{", ";}"], lineHeight, spacing4, _ref2 => {
  let {
    inverted
  } = _ref2;
  return inverted ? blue20 : blue30;
}, _ref3 => {
  let {
    inverted
  } = _ref3;
  return inverted ? blue20 : blue30;
}, grey3, grey3, dir("rtl")`
    background: linear-gradient(
      to left,
      ${_ref4 => {
  let {
    inverted
  } = _ref4;
  return inverted ? blue20 : blue30;
}} 0%,
      ${_ref5 => {
  let {
    inverted
  } = _ref5;
  return inverted ? blue20 : blue30;
}}
        var(--lower-range-percentage),
      ${grey3} var(--lower-range-percentage),
      ${grey3} 100%
    );
  `, handleCss, handleCss, handleCss);
/**
 * The RangeInput allows a user to specify a numeric value which must be no less than a given value, and no more than another given value.
 */

const RangeInput = _ref6 => {
  let {
    name,
    value,
    defaultValue,
    min = "0",
    max = "100",
    step = "1",
    inverted,
    onChange = x => x,
    ...props
  } = _ref6;
  const rangeInputContext = useContext(RangeInputContext);
  const [stateValue, setValue] = useState(value || defaultValue || "0");
  useEffect(() => {
    setValue(value || defaultValue || "0");
  }, [value, defaultValue]);

  const handleChange = event => {
    const targetValue = event.target.value;
    setValue(targetValue);
    onChange(event);
  };

  const calculateBackgroundVars = () => {
    const lowerRangePercentage = (parseFloat(stateValue) - parseFloat((rangeInputContext === null || rangeInputContext === void 0 ? void 0 : rangeInputContext.min) || min)) / (parseFloat((rangeInputContext === null || rangeInputContext === void 0 ? void 0 : rangeInputContext.max) || max) - parseFloat((rangeInputContext === null || rangeInputContext === void 0 ? void 0 : rangeInputContext.min) || min)) * 100;
    return {
      "--lower-range-percentage": `${lowerRangePercentage}%`
    };
  };

  const spreadProps = {
    value: stateValue,
    name,
    min: (rangeInputContext === null || rangeInputContext === void 0 ? void 0 : rangeInputContext.min) || min,
    max: (rangeInputContext === null || rangeInputContext === void 0 ? void 0 : rangeInputContext.max) || max,
    step: (rangeInputContext === null || rangeInputContext === void 0 ? void 0 : rangeInputContext.step) || step,
    inverted,
    onChange: handleChange,
    type: "range",
    ...props
  };
  return /*#__PURE__*/React.createElement(InternalUseOnlyRangeInput, _extends({
    style: calculateBackgroundVars()
  }, spreadProps));
};

RangeInput.propTypes = {
  /** Used to reference the RangeInput in JavaScript, or to reference form data after a form is submitted */
  name: PropTypes.string,

  /** The RangeInput value must be greater than or equal to the min value */
  min: PropTypes.string,

  /** The RangeInput value must be less than or equal to the max value */
  max: PropTypes.string,

  /** The value of the RangeInput */
  value: PropTypes.string,

  /** Callback to execute when the value changes */
  onChange: PropTypes.func,

  /** Default value for uncontrolled use */
  defaultValue: PropTypes.string,

  /** Unless the step is set to the any literal, the input value must be min + an integral multiple of the step  */
  step: PropTypes.string,

  /** Whether to apply styles that help the range input stand out on a dark background */
  inverted: PropTypes.bool
};
export default RangeInput;